import { useState, useEffect } from "react";
import { Modal, Badge, Checkbox } from "@components";
import {
  formatDateShortMonth,
  addressFormatter,
  formatAmount,
  cn,
} from "@utils";
import { TruckIcon, CalendarIcon, WrenchIcon, TagIcon } from "@heroicons/react/16/solid";

export const InvoiceDetailsModal = ({ invoice, isOpen, onClose, orgId, onDisputeLineItems }) => {
  const [selectedLineItemIds, setSelectedLineItemIds] = useState(new Set());

  useEffect(() => {
    setSelectedLineItemIds(new Set());
  }, [invoice?.id]);
  const shouldShowVatDetails = orgId !== "peppard";
  return (
    <Modal
      isOpen={!!invoice ? isOpen : false}
      onClose={onClose}
      maxDesktopWidth="2xl"
      title={
        <div className="flex flex-col gap-2">
          <span className="flex items-center gap-2 flex-wrap">
            <span>{invoice?.number}</span>
            {invoice?.isDisputed && (
              <Badge color="yellow">Disputed</Badge>
            )}
            {invoice?.discountAmount > 0 && (
              <TagIcon className="size-3.5 text-green-700 shrink-0" />
            )}
          </span>
          <div className="flex items-center gap-4 text-xs/5 font-light">
            <span className="flex items-center gap-1.5">
              <CalendarIcon className="size-4" />
              <span>{formatDateShortMonth(invoice?.dueDate)}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <WrenchIcon className="size-4" />
              <span>{invoice?.customerReference}</span>
            </span>
          </div>
          <span className="flex items-center gap-1.5 text-xs/5 font-light">
            <TruckIcon className="size-4" />
            <span>{addressFormatter(invoice?.deliveryAddress)}</span>
          </span>
        </div>
      }
    >
      <div className={cn("p-5 pb-4.5 bg-zinc-50 overflow-y-auto", "sm:pb-0")}>
        <div className="flex flex-col gap-8 sm:gap-4 relative">
          {invoice?.invoiceLineItems.map((lineItem, idx) => (
            <div key={idx} className="flex gap-3 items-start">
              {onDisputeLineItems && !invoice?.isDisputed && (
                <div className="mt-0.5">
                <Checkbox
                  checked={selectedLineItemIds.has(lineItem.id)}
                  onChange={() => {
                    setSelectedLineItemIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(lineItem.id)) {
                        next.delete(lineItem.id);
                      } else {
                        next.add(lineItem.id);
                      }
                      return next;
                    });
                  }}
                />
                </div>
              )}
              <span className="text-xs/6 font-bold">{lineItem.quantity}x</span>
              <div className="flex flex-col gap-2 grow min-w-0">
                <span className="text-xs/6 font-bold">
                  {lineItem.description}
                </span>
                <span className="text-xs/5 font-light">
                  {formatAmount(lineItem.netUnitPrice)} each
                  {shouldShowVatDetails && (
                    <>
                      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                      {formatAmount(lineItem.vatAmount)} Total VAT{" "}
                      {lineItem.vatRate * 100}%
                    </>
                  )}
                </span>
              </div>
              <div className="ml-auto shrink-0">
                <span className="text-xs/6 font-semibold">
                  {formatAmount(lineItem.netAmount)}
                </span>
              </div>
            </div>
          ))}
          <div
            className={cn(
              "sticky bottom-0 left-0 right-0 w-full flex flex-col gap-2 p-4 bg-white rounded-2xl shadow-lg border border-zinc-200",
              "sm:w-auto sm:-mx-5 sm:-mr-5 sm:rounded-none sm:border-0 sm:border-t sm:border-zinc-200 sm:border-solid sm:shadow-[0_-6px_10px_-6px_rgba(0,0,0,0.08)]"
            )}
          >
            <div className="flex justify-between items-center font-light text-zinc-700">
              <span className="text-xs/7">Net total</span>
              <span className="text-xs/7">
                {formatAmount(invoice?.netAmount)}
              </span>
            </div>
            <div className="flex justify-between items-center font-light text-zinc-700">
              <span className="text-xs/7">VAT total</span>
              <span className="text-xs/7">
                {formatAmount(invoice?.vatAmount)}
              </span>
            </div>
            <div className="flex justify-between items-center font-semibold pt-4 border-t border-zinc-600">
              <span className="text-sm/6">Gross invoice total</span>
              <span className="text-sm/6">
                {formatAmount(invoice?.grossAmount)}
              </span>
            </div>
            {onDisputeLineItems && !invoice?.isDisputed && selectedLineItemIds.size > 0 && (
              <button
                type="button"
                onClick={() => {
                  const selected = invoice.invoiceLineItems
                    .filter(li => selectedLineItemIds.has(li.id))
                    .map(li => ({ id: li.id, description: li.description }));
                  onDisputeLineItems(selected);
                }}
                className="w-full text-sm font-medium text-red-600 hover:text-red-700 py-1.5 border-t border-zinc-200 mt-2 pt-3.5 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
              >
                Dispute selected ({selectedLineItemIds.size}) {selectedLineItemIds.size === 1 ? 'item' : 'items'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
